/**
 * Session utilities for questionnaire tracking
 */

// Generate a unique session ID for anonymous users
export const generateSessionId = () => {
  // Use timestamp + random string for uniqueness
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `session_${timestamp}_${randomStr}`;
};

// Get or create session ID from localStorage
export const getSessionId = () => {
  const STORAGE_KEY = 'primeshine_session_id';
  
  let sessionId = localStorage.getItem(STORAGE_KEY);
  
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(STORAGE_KEY, sessionId);
  }
  
  return sessionId;
};

// Clear session ID (useful after successful booking completion)
export const clearSessionId = () => {
  const STORAGE_KEY = 'primeshine_session_id';
  localStorage.removeItem(STORAGE_KEY);
};

// Save questionnaire progress to backend with retry logic
export const saveQuestionnaireProgress = async (stepName, formData, userId = null, retryCount = 0) => {
  const maxRetries = 2;
  
  try {
    const sessionId = getSessionId();
    
    // Validate inputs before sending
    if (!stepName || !formData || typeof formData !== 'object') {
      console.error('Invalid parameters for saveQuestionnaireProgress:', { stepName, formData, userId });
      return false;
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questionnaire/save-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userId && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
      },
      body: JSON.stringify({
        sessionId,
        stepName,
        formData
      })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      console.warn('❌ Failed to save questionnaire progress:', {
        step: stepName,
        error: result.error,
        details: result.details,
        attempt: retryCount + 1
      });
      
      // Retry on certain errors
      if (retryCount < maxRetries && (response.status >= 500 || response.status === 0)) {
        console.log(`🔄 Retrying save operation (attempt ${retryCount + 2}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Progressive delay
        return saveQuestionnaireProgress(stepName, formData, userId, retryCount + 1);
      }
      
      return false;
    }
    
    console.log('✅ Questionnaire progress saved successfully:', {
      step: stepName,
      id: result.data?.id,
      dataKeys: result.data?.dataKeys,
      attempt: retryCount + 1
    });
    return true;
    
  } catch (error) {
    console.error('❌ Network error saving questionnaire progress:', {
      step: stepName,
      error: error.message,
      attempt: retryCount + 1
    });
    
    // Retry on network errors
    if (retryCount < maxRetries) {
      console.log(`🔄 Retrying save operation after network error (attempt ${retryCount + 2}/${maxRetries + 1})`);
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Progressive delay
      return saveQuestionnaireProgress(stepName, formData, userId, retryCount + 1);
    }
    
    return false;
  }
};